import React, {useCallback, useEffect, useMemo} from "react";
import {Root, TabsStyled} from "../../index.styled";
import {useRouter} from "hooks/use-router";
import {Tab} from "@material-ui/core";
import {Col} from "../../../../../../components/layout/layout";
import Select, {SelectOptionProps} from "../../../../../../components/form/select";
import LabelWrapper from "../../../../../../components/label-wrapper";
import {ELabelPos} from "../../../../../../components/form/formik/types";
import SearchInput from "../../../../../../components/input/search-input/search-input";
import MaterialTable from "../../../../../../components/table";
import NoDataFound from "../../../../../../components/table/no-data-found/no-data-found";
import usersColumns from "./user-list.columns";
import {CellButtonsStyled} from "./user-list.styled";
import Button from "../../../../../../components/control/button/button";
import {EButtonSize, EButtonType} from "../../../../../../components/control/button/button.types";
import {users, users as typesUsers} from 'types';
import {ERoutesPrivate, ERoutesType, routes} from "../../../../../../types/routes";
import {setQueryParamsObjToString} from "../../../../../../utils/query-params-transfer";
import useUsersPaginationGet from "../../hooks/use-users-pagination-get";
import {getUsersResponseValidators} from "validators/get-users-response-validators";
import useAppBarTitle from "../../../../../../hooks/use-app-bar-title";
import CircularProgress from "@material-ui/core/CircularProgress";
import TablePagination from "components/table/table-pagination/table-pagination";
import {debounce} from "lodash";
import {TUserQueryParamsFromUrl, TUsersParams} from "../../../../../../types/entities/users";
import wrapUrlWithServerUrl from "../../../../../../utils/fetch/utils/wrap-url-with-server-url";
import endpoints, {EEndpoints} from "../../../../../../config/endpoints";

const searchOptions: SelectOptionProps[] = [
  {
    key: 'name',
    value: 'Name'
  },
  {
    key: 'nexId',
    value: 'Nex ID'
  },
  {
    key: 'phoneNumber',
    value: 'Phone Number (62xxxx)'
  }
]

const pageTitle = 'Users'

const UserListPagination: React.FC = () => {
  useAppBarTitle(pageTitle);
  const { query, history } = useRouter<TUserQueryParamsFromUrl>()

  const [nexId, setNexId] = React.useState<string>('')
  const [name, setName] = React.useState<string>('')
  const [phoneNumber, setPhoneNumber] = React.useState<string>('')
  const [ekycStatus, setEkycStatus] = React.useState<string>('')
  const [status, setStatus] = React.useState<string>('')
  const [page, setPage] = React.useState<number>(0)
  const [limit, setLimit] = React.useState<number>(25)

  const defaultQueryParams: string = setQueryParamsObjToString(query) || setQueryParamsObjToString({
    nexId: '',
    name: '',
    phoneNumber: '',
    ekycStatus: '',
    status: '',
    page: 0,
    limit: 25,
  })
  const [tabState, setTabState] = React.useState<string>('')
  const [searchIdentifier, setSearchIdentifier] = React.useState<string>('name')
  const [searchValue, setSearchValue] = React.useState<string>('')
  const [currentUserList, setCurrentUserList] = React.useState<typesUsers.TUsers[]>()
  const [totalDataCount, setTotalDataCount] = React.useState<number>(0);
  const [queryParams, setQueryParams] = React.useState<string>(defaultQueryParams)

  const { isFetching, refetch, isLoading } = useUsersPaginationGet(queryParams, {
    onSuccess: (response) => {
      const parsedResponse = getUsersResponseValidators(response)
      setCurrentUserList(parsedResponse.users)
      setTotalDataCount(parsedResponse.count)
    },
    enabled: !history.location.search,
    retry: false
  })

  const usersColumnsRendered = useMemo(() => {
    return usersColumns({
      VerifyProfileComponent: ({ rowData }) => (
        <CellButtonsStyled>
          <Button
            onClick={() => {
              // Add handler to verify KYC profile here
            }}
            size={EButtonSize.Small}
          >
            Verify
          </Button>
        </CellButtonsStyled>
      ),
      ControlButtonsComponent: ({ rowData }) => (
        <CellButtonsStyled></CellButtonsStyled>
      ),
    });
  }, []);

  const handleViewUser = (rowData: users.TUsers) => {
    history.push(
      `${routes[ERoutesType.Private][ERoutesPrivate.Users][0]}/${
        rowData.id
      }`,
    );
  };

  useEffect(() => {
    if (tabState === 'PENDINGKYC') {
      setEkycStatus('1')
      setStatus('')
    }
    else if (tabState === 'VERIFIEDKYC') {
      setEkycStatus('2')
      setStatus('')
    }
    else if (tabState === 'SUSPENDED') {
      setEkycStatus('')
      setStatus('SUSPENDED')
    }
    else if (tabState === 'LOCKED') {
      setEkycStatus('')
      setStatus('LOCKED')
    }
    else if (tabState === 'CLOSED') {
      setEkycStatus('')
      setStatus('CLOSED')
    }
    else if (tabState === 'DORMANT') {
      setEkycStatus('')
      setStatus('DORMANT')
    }
    else {
      // All, resets ekycStatus and status
      setEkycStatus('')
      setStatus('')
    }
  }, [tabState])

  const handleTabChange = (e: React.ChangeEvent<{}>, value: string) => {
    setTabState(value);
  }

  const handleSearchChange = () => {
    if (searchIdentifier === 'name') {
      setName(searchValue)
      setPhoneNumber('')
      setNexId('')
    } else if (searchIdentifier === 'nexId') {
      setNexId(searchValue)
      setName('')
      setPhoneNumber('')
    } else if (searchIdentifier === 'phoneNumber') {
      setPhoneNumber(searchValue)
      setName('')
      setNexId('')
    }
  }

  const debouncedSearch = useCallback(
    debounce(() => {
      refetch();
    }, 0),
    [],
  )

  const handleSubmit = useCallback(
    (query: TUsersParams) => {
      const queryParams = {
        page: query.page,
        limit: query.limit,
        nexId: query.nexId ?? '',
        name: query.name ?? '',
        phoneNumber: query.phoneNumber ?? '',
        ekycStatus: query.ekycStatus ?? '',
        status: query.status ?? '',
      };
      const newQueryParams = setQueryParamsObjToString(
        queryParams,
      );

      setQueryParams(newQueryParams);

      history.push({
        search: newQueryParams,
      });
    },
    [history],
  );

  useEffect(() => {
    debouncedSearch()
  }, [refetch, status, ekycStatus, page, limit])

  useEffect(() => {
    const query = {
      nexId,
      name,
      phoneNumber,
      ekycStatus,
      status,
      page,
      limit,
    } as TUsersParams
    setQueryParams(setQueryParamsObjToString(query))
    handleSubmit(query)
  }, [nexId, phoneNumber, name, status, ekycStatus, page, limit, handleSubmit])

  const handleResetSearch = () => {
    setSearchValue('')
    setName('')
    setPhoneNumber('')
    setNexId('')
    const query = {
      nexId,
      name,
      phoneNumber,
      ekycStatus,
      status,
      page,
      limit,
    } as TUsersParams
    setQueryParams(setQueryParamsObjToString(query))
    handleSubmit(query)
    debouncedSearch()
  }

  const downloadFile = async () => {
    const url = wrapUrlWithServerUrl(
      `${endpoints[EEndpoints.users]()}/download?${queryParams}`,
    );
    window.open(url, '_blank');
  };

  return (
    <Root>
      <TabsStyled
        value={tabState}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleTabChange}
      >
        <Tab label={'All'} value={''} />
        <Tab label={'Pending KYC'} value={'PENDINGKYC'} />
        <Tab label={'Verified KYC'} value={'VERIFIEDKYC'} />
        <Tab label={'Suspended'} value={'SUSPENDED'} />
        <Tab label={'Locked'} value={'LOCKED'} />
        <Tab label={'Dormant'} value={'DORMANT'} />
        <Tab label={'Closed'} value={'CLOSED'} />
      </TabsStyled>
      <Col className={'rowrow'} justify={'start'}>
        <Col align={'bottom'} gap={0.2}>
          <LabelWrapper
            label={'Search Value'}
            labelPos={ELabelPos.top}
          >
            <SearchInput
              value={searchValue}
              width={'325px'}
              hasBtnCount={2}
              reset={handleResetSearch}
              onChange={(e) => {
                setSearchValue(e.target.value)
              }}
              refetch={() => {
                handleSearchChange()
                debouncedSearch()
              }}
            />
          </LabelWrapper>
          <Select
            label={'Search by'}
            value={searchIdentifier}
            onChange={(e) => {
                setSearchIdentifier(e.target.value as string)
              }
            }
            options={searchOptions}
          />
          {isFetching && <CircularProgress size={30} />}
        </Col>
      </Col>
      <Col align={'bottom'} justify={'end'}>
        <Col align={'bottom'} justify={'end'} gap={0.2}>
          <Button styleType={EButtonType.Outline} size={EButtonSize.Normal} onClick={downloadFile}>Export as CSV</Button>
        </Col>
        {totalDataCount > 0 && (
          <TablePagination
            page={page}
            totalRows={totalDataCount}
            rowsPerPage={limit}
            setPage={setPage}
            setLimit={setLimit}
          />
        )}
      </Col>
      {totalDataCount > 0 || isLoading ? (
        <MaterialTable<typesUsers.TUsers>
          data={currentUserList}
          columns={usersColumnsRendered}
          isLoading={isLoading}
          onClickRow={handleViewUser}
        />
      ) : (
        <NoDataFound />
      )}
    </Root>
  )
}

export default UserListPagination
